import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, Mail } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  getTailoringOrderInvoices,
  getTailoringOrder,
  getTailoringPayments,
  updateTailoringOrderStatus,
  updateTailoringOrder,
  cancelTailoringOrder,
  downloadTailoringReceipt,
  sendTailoringReceipt,
  checkCashRegister,
  updateSubOrderStatus,
} from "../lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "PENDIENTE_PAGO",    label: "Pendiente de pago" },
  { value: "PRE_PRODUCCION",    label: "Pre-producción" },
  { value: "EN_PRODUCCION",     label: "En producción" },
  { value: "CONFECCIONADO",     label: "Confeccionado" },
  { value: "LISTO_PARA_ENTREGA", label: "Listo para entrega" },
  { value: "ENTREGADO",         label: "Entregado" },
  { value: "CANCELADO",         label: "Cancelado" },
];

const STATUS_COLORS = {
  PENDIENTE_PAGO:     "bg-yellow-100 text-yellow-800",
  PRE_PRODUCCION:     "bg-blue-100 text-blue-800",
  EN_PRODUCCION:      "bg-indigo-100 text-indigo-800",
  CONFECCIONADO:      "bg-purple-100 text-purple-800",
  LISTO_PARA_ENTREGA: "bg-green-100 text-green-800",
  ENTREGADO:          "bg-slate-100 text-slate-700",
  CANCELADO:          "bg-red-100 text-red-700",
};

const money = (n) =>
  Number(n || 0).toLocaleString("es-DO", {
    style: "currency",
    currency: "DOP",
  });

const TailoringOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [editMeas, setEditMeas] = useState({});
  const [editChanges, setEditChanges] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [orderInvoices, setOrderInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cajaOpen, setCajaOpen] = useState(true);
  const [cajaStale, setCajaStale] = useState(false);
  const [cajaStaleDate, setCajaStaleDate] = useState('');

  const [statusToSet, setStatusToSet] = useState("");
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [askSaveEmail, setAskSaveEmail] = useState(null);
  const [printingReceipt, setPrintingReceipt] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [changingSubOrder, setChangingSubOrder] = useState(null);

  const statusLabel = useMemo(() => {
    const m = new Map(STATUS_OPTIONS.map((x) => [x.value, x.label]));
    return (s) => m.get(s) || s;
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [o, p, inv] = await Promise.all([
        getTailoringOrder(id),
        getTailoringPayments(id),
        getTailoringOrderInvoices(id),
      ]);
      setOrder(o);
      setEditMeas(o?.customization?.measurements || {});
      setEditChanges(o?.customization?.changes_requested || "");
      setEditNotes(o?.notes || "");
      setPayments(p || []);
      setOrderInvoices(inv || []);
      setStatusToSet(o?.status || "");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cargando pedido");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
    checkCashRegister().then(res => {
      setCajaOpen(!res.required || res.open);
      setCajaStale(res.stale || false);
      setCajaStaleDate(res.stale_date || '');
    }).catch(() => setCajaOpen(true));
  }, [id]);

  const subOrders   = order?.sub_orders || [];
  const isNewFormat = subOrders.length > 0;

  const doSubOrderStatusChange = async (subOrderId, newStatus) => {
    try {
      setChangingSubOrder(subOrderId);
      const updated = await updateSubOrderStatus(id, subOrderId, newStatus);
      setOrder(updated);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error actualizando estado');
    } finally {
      setChangingSubOrder(null);
    }
  };

  const total = Number(order?.pricing?.total_amount || 0);
  const paid = Number(order?.pricing?.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const services = order?.services || [];
  const servicesTotal = Number(
    order?.services_total ||
      services.reduce((s, item) => s + Number(item.subtotal || 0), 0),
  );
  const invoiceHistory =
    (order?.invoices || []).length > 0 ? order.invoices : payments;
  const paymentStatus = order?.pricing?.payment_status;
  const isPaid = paymentStatus === "PAGADO";
  const hasBalance = balance > 0;

  const doStatus = async () => {
    try {
      if (!statusToSet) return;

      if (statusToSet === "CANCELADO") {
        setCancelReason("");
        setCancelOpen(true);
        return;
      }

      const updated = await updateTailoringOrderStatus(id, statusToSet);
      setOrder(updated);
      toast.success("Estatus actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cambiando estatus");
    }
  };

  const confirmCancel = async () => {
    try {
      const reason = cancelReason.trim();
      if (!reason) return toast.error("La razón es obligatoria");

      await cancelTailoringOrder(id, reason);
      await load();
      setCancelOpen(false);
      toast.success("Pedido cancelado");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cancelando pedido");
      setStatusToSet(order?.status || "");
    }
  };

  const openInvoiceModal = () => {
    setInvoiceForm({
      amount: String(balance || ""),
      payment_method: "efectivo",
      reference: "",
    });
    setInvoiceOpen(true);
  };

  const canEdit =
    order?.status !== "ENTREGADO" && order?.status !== "CANCELADO";

  const saveEdits = async () => {
    try {
      const payload = {
        measurements: editMeas,
        changes_requested: editChanges,
        notes: editNotes,
      };
      await updateTailoringOrder(id, payload);
      await load();
      setEditing(false);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error guardando cambios");
    }
  };

  if (!order) {
    return (
      <div className="text-slate-600">
        {loading ? "Cargando..." : "Pedido no encontrado"}
      </div>
    );
  }

  const handlePrintReceipt = async () => {
    setPrintingReceipt(true);
    try {
      const blob = await downloadTailoringReceipt(id);

      // Si el servidor devolvió un error JSON en vez de PDF, mostrarlo
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        toast.error(json.error || "Error generando comprobante");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      // Usar <a> en vez de window.open para evitar bloqueo del navegador
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (err) {
      console.error("Error generando comprobante:", err);
      toast.error(err.response?.data?.error || err.message || "Error generando comprobante");
    } finally {
      setPrintingReceipt(false);
    }
  };

  const doSendReceipt = async (email) => {
    setSendingEmail(true);
    try {
      await sendTailoringReceipt(id, email);
      toast.success(`Comprobante enviado a ${email}`);
      setSendEmailModal(false);
      setEmailTo("");
    } catch (err) {
      console.error("Error enviando correo:", err);
      toast.error(err.response?.data?.error || err.message || "Error enviando correo. Verifica la configuración SMTP en Ajustes.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    const email = emailTo.trim();
    if (!email || !email.includes("@")) {
      toast.error("Ingresa un correo válido");
      return;
    }
    const registeredEmail = order?.customer_email || "";
    if (order?.customer_id && email !== registeredEmail) {
      setAskSaveEmail(email);
      return;
    }
    await doSendReceipt(email);
  };

  const confirmSaveEmail = async (doSave) => {
    const email = askSaveEmail;
    setAskSaveEmail(null);
    if (doSave && order?.customer_id) {
      try {
        await updateCustomer(order.customer_id, { email });
        toast.success("Correo guardado en el perfil del cliente");
      } catch (e) { console.error(e); }
    }
    await doSendReceipt(email);
  };

  return (
    <div className="p-4 space-y-6  max-w-7xl mx-auto" data-testid="tailoring-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Pedido: {order.code ? `${order.code}` : `#${order.id}`}
          </h1>

          <p className="text-slate-600">
            Cliente: <span className="font-medium">{order.customer_name}</span>
            {isNewFormat
              ? <> · <span className="font-medium">{subOrders.length} prenda{subOrders.length !== 1 ? 's' : ''}</span></>
              : <> · Producto: <span className="font-medium">{order?.product_ref?.name}</span></>
            }
            {" "}· Vendedor: <span className="font-medium">{order?.seller_name}</span>
          </p>
        </div>

     
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumen</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-slate-500 text-sm">Estatus</div>
            <div className="mt-1">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[order.status] || "bg-slate-100 text-slate-600"}`}>
                {statusLabel(order.status)}
              </span>
            </div>
            {order?.status === "CANCELADO" && (
              <div className="mt-2 text-sm text-red-700">
                <span className="font-medium">Motivo:</span>{" "}
                {order?.cancel_reason || "-"}
              </div>
            )}
          </div>
          <div>
            <div className="text-slate-500 text-sm">Total</div>
            <div className="font-medium">{money(total)}</div>
          </div>
          <div>
            <div className="text-slate-500 text-sm">Pagado</div>
            <div className="font-medium">
              {money(paid)} ({order?.pricing?.payment_status})
            </div>
          </div>
          <div>
            <div className="text-slate-500 text-sm">Pendiente</div>
            <div className="font-medium">{money(balance)}</div>
          </div>

          <div className="md:col-span-4">
            <div className="text-slate-500 text-sm">Entrega</div>
            <div className="font-medium">
              {order?.delivery?.due_date
                ? new Date(order.delivery.due_date).toLocaleDateString("es-DO")
                : "-"}
            </div>
          </div>


  <div className="md:col-span-4 flex flex-row items-center justify-center gap-3 mt-2">
    {!isPaid && (
      <Button
        onClick={() => {
          if (!cajaOpen && cajaStale) { toast.warning(`Cierra el turno del ${cajaStaleDate} antes de facturar`); return; }
          if (!cajaOpen) { toast.warning('Abre un turno de caja para facturar'); return; }
          window.location.href = `/pos?tailoring_order_id=${order.id}`;
        }}
        title={cajaOpen ? "Facturar Pedido" : cajaStale ? `Turno del ${cajaStaleDate} sin cerrar` : "Caja cerrada"}
        className={`w-44 ${!cajaOpen ? 'bg-slate-400' : hasBalance ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
      >
        {cajaOpen ? '💰 Facturar' : '🔒 Facturar'}
      </Button>
    )}
    {/* Imprimir y Enviar solo visibles cuando se ha pagado al menos el 50% */}
    {(total > 0 && paid >= total * 0.5) && (
      <>
        <Button
          variant="outline"
          onClick={handlePrintReceipt}
          disabled={printingReceipt}
          title="Imprimir Comprobante"
          className="flex items-center gap-2 w-44"
        >
          <Printer className="w-4 h-4" />
          {printingReceipt ? "Generando..." : "Imprimir"}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
        setEmailTo(order?.customer_email || "");
        setSendEmailModal(true);
      }}
          title="Enviar por Correo"
          className="flex items-center gap-2 w-44"
        >
          <Mail className="w-4 h-4" />
          Enviar
        </Button>
      </>
    )}
  </div>
        </CardContent>
      </Card>

      {/* ── Prendas: nuevo formato ── */}
      {isNewFormat ? (
        <Card>
          <CardHeader><CardTitle className="text-lg">Prendas ({subOrders.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {subOrders.map((so, idx) => {
              const soStatus = so.status || 'PENDIENTE';
              const soStatusColors = {
                PENDIENTE:  'bg-slate-100 text-slate-600 border-slate-200',
                EN_PROCESO: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                LISTO:      'bg-green-100 text-green-700 border-green-200',
              };
              const soNextStatus = { PENDIENTE: 'EN_PROCESO', EN_PROCESO: 'LISTO', LISTO: null };
              const soNextLabel  = { PENDIENTE: '→ En proceso', EN_PROCESO: '→ Listo' };
              const nextSt       = soNextStatus[soStatus];
              const isUpdating   = changingSubOrder === so.id;
              const isFinal      = order?.status === 'ENTREGADO' || order?.status === 'CANCELADO';

              return (
                <div key={so.id || idx} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Header prenda */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="bg-cyan-100 text-cyan-800 font-bold text-xs rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                        {so.letter || String.fromCharCode(65 + idx)}
                      </span>
                      <span className="font-semibold text-sm text-slate-900">{so.product_name || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${soStatusColors[soStatus] || ''}`}>
                        {soStatus === 'PENDIENTE' ? 'Pendiente' : soStatus === 'EN_PROCESO' ? 'En proceso' : 'Listo'}
                      </span>
                      {nextSt && !isFinal && (
                        <button type="button"
                          onClick={() => doSubOrderStatusChange(so.id, nextSt)}
                          disabled={isUpdating}
                          className="text-xs px-2 py-0.5 border border-slate-300 rounded hover:bg-cyan-50 hover:border-cyan-400 hover:text-cyan-700 transition-colors disabled:opacity-50">
                          {isUpdating ? '...' : soNextLabel[soStatus]}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Body prenda */}
                  <div className="px-4 py-3 space-y-3 text-sm">
                    {/* Precios */}
                    <div className="space-y-0.5">
                      {Number(so.item_price) > 0 && (
                        <div className="flex justify-between text-slate-700">
                          <span>• {so.product_name} <span className="text-xs text-slate-400">(confección)</span></span>
                          <span className="font-medium">{money(so.item_price)}</span>
                        </div>
                      )}
                      {(so.services || []).map(s => (
                        <div key={s.product_id} className="flex justify-between text-slate-700">
                          <span>• {s.name} ×{s.quantity}</span>
                          <span className="font-medium">{money(s.subtotal)}</span>
                        </div>
                      ))}
                      <div className="flex justify-end pt-1 border-t border-slate-100">
                        <span className="font-semibold text-slate-800">Subtotal: {money(so.subtotal)}</span>
                      </div>
                    </div>

                    {/* Medidas */}
                    {so.measurements && Object.values(so.measurements).some(v => v) && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Medidas (cm)</p>
                        <div className="grid grid-cols-5 gap-1.5">
                          {['pecho','cintura','cadera','largo','manga'].map(k => (
                            <div key={k} className="border rounded p-1.5 bg-white text-center">
                              <div className="text-xs text-slate-400 capitalize">{k}</div>
                              <div className="font-medium text-sm">{so.measurements[k] || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cambios solicitados */}
                    {so.changes_requested && (
                      <div className="bg-slate-50 rounded p-2 border border-slate-100">
                        <p className="text-xs text-slate-400 mb-0.5">Cambios solicitados</p>
                        <p className="text-slate-700">{so.changes_requested}</p>
                      </div>
                    )}

                    {/* Instrucciones taller */}
                    {so.work_notes && (
                      <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-2">
                        "{so.work_notes}"
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Formato legacy: Servicios + Confección ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Servicios adicionales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-600">
                      <th className="py-2 pr-4">Servicio</th>
                      <th className="py-2 pr-4">Cantidad</th>
                      <th className="py-2 pr-4">Precio</th>
                      <th className="py-2 pr-4">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => (
                      <tr key={s.product_id} className="border-b">
                        <td className="py-2 pr-4">{s.name}</td>
                        <td className="py-2 pr-4">{s.quantity}</td>
                        <td className="py-2 pr-4">{money(s.unit_price)}</td>
                        <td className="py-2 pr-4">{money(s.subtotal)}</td>
                      </tr>
                    ))}
                    {services.length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-500">No hay servicios adicionales.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-sm text-slate-600">
                Total servicios: <span className="font-medium">{money(servicesTotal)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Confección / Cambios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-end">
                <div className="flex gap-2">
                  {!editing && (
                    <Button variant="outline" onClick={() => setEditing(true)} disabled={!canEdit}>Editar</Button>
                  )}
                  {editing && (
                    <>
                      <Button onClick={saveEdits}>Guardar</Button>
                      <Button variant="outline" onClick={() => {
                        setEditing(false);
                        setEditMeas(order?.customization?.measurements || {});
                        setEditChanges(order?.customization?.changes_requested || "");
                        setEditNotes(order?.notes || "");
                      }}>Cancelar</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                {Object.entries(editing ? editMeas || {} : order?.customization?.measurements || {}).map(([k, v]) => (
                  <div key={k} className="border rounded p-2 bg-white">
                    <div className="text-slate-500 capitalize">{k}</div>
                    {!editing ? (
                      <div className="font-medium">{v}</div>
                    ) : (
                      <Input value={String(v ?? "")}
                        onChange={(e) => setEditMeas(prev => ({ ...prev, [k]: e.target.value }))}
                        placeholder="0" />
                    )}
                  </div>
                ))}
              </div>

              <div className="border rounded p-3 bg-white">
                <div className="text-slate-500 text-sm">Cambios requeridos</div>
                {!editing ? (
                  <div className="text-slate-800">{order?.customization?.changes_requested || "-"}</div>
                ) : (
                  <Input value={editChanges} onChange={(e) => setEditChanges(e.target.value)} placeholder="Describe los cambios..." />
                )}
              </div>

              <div className="border rounded p-3 bg-white">
                <div className="text-slate-500 text-sm">Notas</div>
                {!editing ? (
                  <div className="text-slate-800">{order?.notes || "-"}</div>
                ) : (
                  <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Observaciones..." />
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Estatus y acciones (siempre visible) ── */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Estatus y acciones</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Si no pagó 50%, solo puede cancelar */}
              {paid < total * 0.5 && order?.status === "PENDIENTE_PAGO" && (
                <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                  ⚠️ Se requiere al menos 50% de pago para cambiar de estado. Solo se permite cancelar.
                </p>
              )}
              <Select value={statusToSet} onValueChange={setStatusToSet}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Cambiar estatus" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter((s) => {
                    // Si no pagó 50% solo se puede cancelar (o mantener estado actual)
                    const has50 = total > 0 && paid >= total * 0.5;
                    if (!has50 && s.value !== "CANCELADO" && s.value !== order?.status) return false;
                    // No mostrar el estado actual como opción de cambio (excepto PRE_PRODUCCION que es auto)
                    if (s.value === "PRE_PRODUCCION" && order?.status !== "PRE_PRODUCCION") return false;
                    return true;
                  }).map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={doStatus}
                disabled={order?.status === "CANCELADO"}
              >
                Actualizar
              </Button>
            </div>

            {/* Email modal */}
            <Dialog open={sendEmailModal} onOpenChange={setSendEmailModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enviar comprobante por correo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {order?.customer_email && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                      Correo registrado: <strong>{order.customer_email}</strong>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>
                      {order?.customer_email ? "Enviar a otro correo (opcional)" : "Correo electrónico del cliente"}
                    </Label>
                    <Input
                      type="email"
                      placeholder={order?.customer_email || "cliente@ejemplo.com"}
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSendEmail()}
                      autoFocus={!order?.customer_email}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSendEmailModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingEmail ? "Enviando..." : "Enviar comprobante"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Modal: ¿Guardar correo en perfil? */}
            <Dialog open={!!askSaveEmail} onOpenChange={() => setAskSaveEmail(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>¿Guardar correo en el perfil?</DialogTitle>
                </DialogHeader>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    ¿Deseas guardar <strong>{askSaveEmail}</strong> como correo de{" "}
                    <strong>{order?.customer_name}</strong> para futuros envíos?
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => confirmSaveEmail(false)} disabled={sendingEmail}>
                    No, solo enviar
                  </Button>
                  <Button onClick={() => confirmSaveEmail(true)} disabled={sendingEmail}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    {sendingEmail ? "Enviando..." : "Guardar y enviar"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancelar pedido</DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                  <div className="text-slate-600 text-sm">
                    Indica el motivo. Esta acción bloquea modificaciones y
                    abonos.
                  </div>

                  <div className="space-y-1">
                    <Label>Razón</Label>
                    <Input
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Ej: Cliente canceló / error en medidas / etc."
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCancelOpen(false);
                        setStatusToSet(order?.status || "");
                      }}
                    >
                      Volver
                    </Button>
                    <Button variant="destructive" onClick={confirmCancel}>
                      Confirmar cancelación
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

     

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de pagos </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Factura</th>
                  <th className="py-2 pr-4">Método</th>
                  <th className="py-2 pr-4">Monto</th>
                </tr>
              </thead>
              <tbody>
                {orderInvoices.map((inv) => {
                  const methodLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', credito: 'Crédito' };
                  const methodText = inv.payment_methods?.length > 0
                    ? inv.payment_methods.map(pm => methodLabels[pm.method] || pm.method).join(' + ')
                    : methodLabels[inv.payment_method] || inv.payment_method || '-';
                  return (
                  <tr key={inv.id} className="border-b">
                    <td className="py-2 pr-4">
                      {inv.created_at ? new Date(inv.created_at).toLocaleString("es-DO") : ""}
                    </td>
                    <td className="py-2 pr-4">
                      {inv.invoice_number || inv.code || inv.id}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{methodText}</td>
                    <td className="py-2 pr-4">
                      {money(inv.total || inv.amount_paid || 0)}
                    </td>
                  </tr>
                  );
                })}

                {orderInvoices.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-slate-500">
                      No hay facturas registradas para este pedido.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TailoringOrderDetail;